import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE_URL =
  "https://script.google.com/macros/s/AKfycbzM7kZKi7IpgcrR184JyTtROpJtakfJ0dUDbEOiDM5i102T4gTRMNz-Mwy2c1oe4Ge1/exec";

const API_KEY = "ap_43maple";

const SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const STOCK_DETAIL_PATH = "/stocks2";
const ETF_DETAIL_PATH = "/etfs";

function apiUrl(params) {
  const search = new URLSearchParams({
    key: API_KEY,
    ...params,
  });

  return `${API_BASE_URL}?${search.toString()}`;
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;

  const clean = String(value)
    .replace(/[$,%]/g, "")
    .replace(/,/g, "")
    .replace(/[()]/g, "")
    .trim();

  if (!clean || clean === "-" || clean === "—") return null;

  const n = Number(clean);
  return Number.isFinite(n) ? n : null;
}

function normalizeTicker(ticker) {
  return String(ticker || "").trim().toUpperCase();
}

function tickerAliases(ticker) {
  const t = normalizeTicker(ticker);
  const aliases = new Set([t]);

  if (t.includes(".")) aliases.add(t.replace(/\./g, "-"));
  if (t.includes("-")) aliases.add(t.replace(/-/g, "."));

  if (t === "BRK.B") aliases.add("BRK-B");
  if (t === "BRK-B") aliases.add("BRK.B");

  return Array.from(aliases);
}

/**
 * Use this for stock_screener.json's pctAbove52WeekLow field.
 *
 * In your screener JSON, this field is already a decimal ratio:
 * 0.10 = 10%
 * 1.00 = 100%
 * 10.00 = 1000%
 */
function normalizeRatioDistance(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;

  // Sanity cap: 50 = 5000% above low.
  // Anything above this is probably corrupted data.
  if (n > 50) return null;

  return n;
}

/**
 * Use this for Google Sheet / Apps Script percent-style fields.
 *
 * Handles:
 * "10%" => 0.10
 * "200%" => 2.00
 * "1000%" => 10.00
 * 10 => 0.10
 * 200 => 2.00
 * 1000 => 10.00
 * 0.10 => 0.10
 * 2.00 => 2.00
 * 10.00 => 10.00, if Apps Script getValues returned a true percentage decimal
 */
function normalizePercentDistance(value) {
  if (value === null || value === undefined || value === "") return null;

  const raw = String(value).trim();
  if (!raw || raw === "-" || raw === "—") return null;

  const hasPercentSign = raw.includes("%");
  const n = parseNumber(value);

  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;

  // Explicit percent strings.
  if (hasPercentSign) return n / 100;

  // If Apps Script returns a real percentage cell via getValues(),
  // 0.10 = 10%, 1 = 100%, 10 = 1000%.
  // But if the Sheet stores whole-number percentages, 10 = 10%.
  //
  // Since your Sheet/API values have been inconsistent, this function is only
  // used as fallback. The primary source for stocks is stock_screener.json.
  if (n > 50) return n / 100;

  return n;
}

function getFirstValidRatio(...values) {
  for (const value of values) {
    const n = normalizeRatioDistance(value);
    if (Number.isFinite(n)) return n;
  }

  return null;
}

function getFirstValidPercent(...values) {
  for (const value of values) {
    const n = normalizePercentDistance(value);
    if (Number.isFinite(n)) return n;
  }

  return null;
}

function formatDateTime(value) {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatMoney(value) {
  const n = parseNumber(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatPctAboveLow(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";

  const pct = n * 100;

  if (Math.abs(pct) >= 1000) return `${pct.toFixed(0)}%`;
  if (Math.abs(pct) >= 100) return `${pct.toFixed(0)}%`;
  return `${pct.toFixed(1)}%`;
}

function getLowColorStyle(pctAbove52WeekLow) {
  const n = Number(pctAbove52WeekLow);

  if (!Number.isFinite(n)) {
    return {
      background: "#f8fafc",
      borderColor: "#e2e8f0",
      color: "#475569",
      boxShadow: "none",
    };
  }

  const capped = Math.max(0, Math.min(0.5, n));
  const ratio = capped / 0.5;
  const hue = 120 - ratio * 120;

  const base = {
    background: `hsl(${hue}, 82%, 88%)`,
    borderColor: `hsl(${hue}, 70%, 62%)`,
    color: "#0f172a",
  };

  if (n <= 0.03) {
    return {
      ...base,
      borderColor: "#16a34a",
      boxShadow:
        "0 0 0 2px rgba(34, 197, 94, 0.75), 0 0 12px rgba(34, 197, 94, 0.55)",
    };
  }

  if (n >= 0.5) {
    return {
      ...base,
      borderColor: "#dc2626",
      boxShadow:
        "0 0 0 2px rgba(239, 68, 68, 0.65), 0 0 12px rgba(239, 68, 68, 0.48)",
    };
  }

  return {
    ...base,
    boxShadow: "0 3px 8px rgba(15, 23, 42, 0.06)",
  };
}

function getDistanceBucket(value) {
  const n = Number(value);

  if (!Number.isFinite(n)) return "No data";
  if (n <= 0.03) return "At low";
  if (n <= 0.05) return "Very close";
  if (n <= 0.15) return "Close";
  if (n <= 0.30) return "Mid";
  if (n <= 0.50) return "Extended";
  if (n <= 1.00) return "Very extended";
  return "Extreme";
}

export default function ThemeMap() {
  const [mapData, setMapData] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [etfs, setEtfs] = useState([]);
  const [screenerRows, setScreenerRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [hideMissingData, setHideMissingData] = useState(false);
  const [maxPctAboveLow, setMaxPctAboveLow] = useState("");

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        if (!API_BASE_URL || API_BASE_URL.includes("?")) {
          throw new Error(
            "API_BASE_URL should be the clean /exec URL only, with no ?type=map attached."
          );
        }

        if (!API_KEY) {
          throw new Error("Missing Apps Script API key.");
        }

        const [mapRes, stocksRes, etfsRes, screenerRes] = await Promise.all([
          fetch(apiUrl({ type: "map" }), { cache: "no-store" }),
          fetch(apiUrl({ type: "stocks", view: "list" }), { cache: "no-store" }),
          fetch(apiUrl({ type: "etfs", view: "list" }), { cache: "no-store" }),
          fetch(SCREENER_URL, { cache: "no-store" }),
        ]);

        const [mapJson, stocksJson, etfsJson] = await Promise.all([
          mapRes.json(),
          stocksRes.json(),
          etfsRes.json(),
        ]);

        if (mapJson?.error) throw new Error(mapJson.error);
        if (stocksJson?.error) throw new Error(stocksJson.error);
        if (etfsJson?.error) throw new Error(etfsJson.error);

        setMapData(mapJson);
        setStocks(Array.isArray(stocksJson) ? stocksJson : []);
        setEtfs(Array.isArray(etfsJson) ? etfsJson : []);

        if (screenerRes.ok) {
          const screenerText = await screenerRes.text();

          if (!screenerText.trim().startsWith("<")) {
            const screenerJson = JSON.parse(screenerText);
            setScreenerRows(Array.isArray(screenerJson.data) ? screenerJson.data : []);
          }
        }
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load theme map.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  const dataByTicker = useMemo(() => {
    const map = {};

    // Fallback source: Apps Script stock data.
    // These fields are treated as percent-style because they come from the Sheet.
    for (const row of stocks) {
      const ticker = normalizeTicker(row.Symbol);
      if (!ticker) continue;

      const pctFromAppsScript = getFirstValidPercent(
        row["52LowCh"],
        row["52 Low Ch"],
        row["52 Wk Low Change"],
        row["52 Week Low Change"]
      );

      const enriched = {
        type: "stock",
        ticker,
        name: row.Stock || "",
        price: row.Price,
        pctAbove52WeekLow: pctFromAppsScript,
        source: "apps-script-stocks",
        rawPctValues: {
          apps_52LowCh: row["52LowCh"],
          apps_52LowCh_spaced: row["52 Low Ch"],
          apps_52WkLowChange: row["52 Wk Low Change"],
          apps_52WeekLowChange: row["52 Week Low Change"],
        },
        raw: row,
      };

      for (const alias of tickerAliases(ticker)) {
        map[alias] = enriched;
      }
    }

    // Fallback source: Apps Script ETF data.
    for (const row of etfs) {
      const ticker = normalizeTicker(row.Symbol);
      if (!ticker) continue;

      const pctFromEtfApi = getFirstValidPercent(
        row["52 Wk Low Change"],
        row["52 Wk Low Change%"],
        row["52LowCh"],
        row["52 Low Ch"],
        row["52 Week Low Change"]
      );

      const enriched = {
        type: "etf",
        ticker,
        name: row.ETF || "",
        price: row.Price,
        pctAbove52WeekLow: pctFromEtfApi,
        source: "apps-script-etfs",
        rawPctValues: {
          etf_52WkLowChange: row["52 Wk Low Change"],
          etf_52WkLowChangePct: row["52 Wk Low Change%"],
          etf_52LowCh: row["52LowCh"],
        },
        raw: row,
      };

      for (const alias of tickerAliases(ticker)) {
        map[alias] = enriched;
      }
    }

    // Primary source: GitHub stock_screener.json.
    // pctAbove52WeekLow here is a ratio, so 10 = 1000%.
    for (const row of screenerRows) {
      const ticker = normalizeTicker(row.ticker || row.Symbol || row.symbol);
      if (!ticker) continue;

      const existing = map[ticker];

      const pctFromScreenerRatio = getFirstValidRatio(
        row.pctAbove52WeekLow,
        row["pctAbove52WeekLow"]
      );

      const pctFromScreenerPercentFallback = getFirstValidPercent(
        row["52LowCh"],
        row["52 Wk Low Change"],
        row["52 Low Ch"],
        row["52 Week Low Change"]
      );

      const finalPct =
        pctFromScreenerRatio ??
        pctFromScreenerPercentFallback ??
        existing?.pctAbove52WeekLow ??
        null;

      const finalSource =
        Number.isFinite(pctFromScreenerRatio)
          ? "stock_screener_json_ratio"
          : Number.isFinite(pctFromScreenerPercentFallback)
          ? "stock_screener_json_percent_fallback"
          : existing?.source || "none";

      const enriched = {
        type: existing?.type || "stock",
        ticker,
        name: row.name || row.Stock || existing?.name || "",
        price: row.price ?? row.Price ?? existing?.price ?? null,
        pctAbove52WeekLow: finalPct,
        source: finalSource,
        rawPctValues: {
          screener_pctAbove52WeekLow: row.pctAbove52WeekLow,
          screener_pctAbove52WeekLow_bracket: row["pctAbove52WeekLow"],
          screener_52LowCh: row["52LowCh"],
          screener_52WkLowChange: row["52 Wk Low Change"],
          existing: existing?.rawPctValues || null,
        },
        raw: {
          screener: row,
          existing: existing?.raw || null,
        },
      };

      for (const alias of tickerAliases(ticker)) {
        map[alias] = enriched;
      }
    }

    return map;
  }, [stocks, etfs, screenerRows]);

  const categories = useMemo(() => {
    const rawCategories = Array.isArray(mapData?.categories)
      ? mapData.categories
      : [];

    const q = search.trim().toLowerCase();

    const maxPctInput = parseNumber(maxPctAboveLow);
    const maxPctDecimal =
      Number.isFinite(maxPctInput) && maxPctInput >= 0
        ? maxPctInput / 100
        : null;

    return rawCategories
      .map((category) => {
        const tickers = Array.isArray(category.tickers) ? category.tickers : [];

        const enrichedTickers = tickers
          .map((item) => {
            const ticker = normalizeTicker(item.ticker);

            const match =
              tickerAliases(ticker)
                .map((alias) => dataByTicker[alias])
                .find(Boolean) || null;

            return {
              ...item,
              ticker,
              match,
              name: match?.name || "",
              price: match?.price ?? null,
              type: match?.type || "unknown",
              source: match?.source || "none",
              rawPctValues: match?.rawPctValues || null,
              pctAbove52WeekLow: Number.isFinite(match?.pctAbove52WeekLow)
                ? match.pctAbove52WeekLow
                : null,
              bucket: getDistanceBucket(match?.pctAbove52WeekLow),
            };
          })
          .filter((item) => {
            const hasPct = Number.isFinite(item.pctAbove52WeekLow);

            if (hideMissingData && !hasPct) {
              return false;
            }

            if (Number.isFinite(maxPctDecimal)) {
              if (!hasPct) return false;
              if (item.pctAbove52WeekLow > maxPctDecimal) return false;
            }

            if (!q) return true;

            return (
              item.ticker.toLowerCase().includes(q) ||
              String(item.name || "").toLowerCase().includes(q) ||
              String(category.name || "").toLowerCase().includes(q)
            );
          });

        return {
          ...category,
          tickers: enrichedTickers,
        };
      })
      .filter((category) => category.tickers.length > 0);
  }, [mapData, dataByTicker, search, hideMissingData, maxPctAboveLow]);

  const maxRows = useMemo(() => {
    return categories.reduce(
      (max, category) => Math.max(max, category.tickers.length),
      0
    );
  }, [categories]);

  if (loading) {
    return (
      <div style={pageShell}>
        <div style={messageBox}>Loading theme map...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageShell}>
        <div style={errorBox}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageShell}>
      <section style={heroCard}>
        <div style={heroTextWrap}>
          <div>
            <div style={eyebrow}>Majorah Theme Map</div>
            <h1 style={title}>Stock Theme Map</h1>
            <p style={subtitle}>
              Green = near 52-week low. Yellow = mid-range. Red = extended.
              Green glow = within 3% of low. Red glow = 50%+ above low.
            </p>
            <p style={metaText}>Sheet updated: {formatDateTime(mapData?.asOf)}</p>
          </div>

          <div style={controls}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ticker/theme..."
              style={searchInput}
            />

            <input
              value={maxPctAboveLow}
              onChange={(e) => setMaxPctAboveLow(e.target.value)}
              placeholder="Max % above low"
              type="number"
              min="0"
              step="1"
              style={smallInput}
              title="Example: 10 means only show stocks within 10% of 52-week low"
            />

            <button
              type="button"
              onClick={() => setHideMissingData((prev) => !prev)}
              style={toggleButton(hideMissingData)}
            >
              {hideMissingData ? "With Data Only" : "Hide Missing"}
            </button>

            {maxPctAboveLow !== "" ? (
              <button
                type="button"
                onClick={() => setMaxPctAboveLow("")}
                style={clearButton}
              >
                Clear %
              </button>
            ) : null}
          </div>
        </div>

        <div style={miniLegend}>
          <span>Near low</span>
          <div style={gradientBar} />
          <span>Extended</span>
        </div>
      </section>

      <section style={mapCard}>
        {categories.length === 0 ? (
          <div style={messageBox}>No matching tickers found.</div>
        ) : (
          <div style={tableWrap}>
            <table style={mapTable}>
              <thead>
                <tr>
                  {categories.map((category) => (
                    <th key={category.name} style={themeHeader}>
                      {category.name}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {Array.from({ length: maxRows }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {categories.map((category) => {
                      const item = category.tickers[rowIndex];

                      return (
                        <td key={`${category.name}-${rowIndex}`} style={mapCell}>
                          {item ? <TickerTile item={item} /> : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function TickerTile({ item }) {
  const colorStyle = getLowColorStyle(item.pctAbove52WeekLow);

  const detailPath =
    item.type === "etf"
      ? `${ETF_DETAIL_PATH}/${encodeURIComponent(item.ticker)}`
      : `${STOCK_DETAIL_PATH}/${encodeURIComponent(item.ticker)}`;

  const rawDebug =
    item.rawPctValues ? ` | raw: ${JSON.stringify(item.rawPctValues)}` : "";

  return (
    <Link
      to={detailPath}
      style={{ ...tickerTile, ...colorStyle }}
      title={`${item.ticker} ${item.name ? `- ${item.name}` : ""} | ${formatPctAboveLow(
        item.pctAbove52WeekLow
      )} above 52W low | ${item.bucket} | source: ${item.source}${rawDebug}`}
    >
      <div style={tickerTopRow}>
        <span style={tickerSymbol}>{item.ticker}</span>
        <span style={typePill}>{item.type === "unknown" ? "?" : item.type.toUpperCase()}</span>
      </div>

      <div style={priceRow}>
        <span>{formatMoney(item.price)}</span>
        <span>{formatPctAboveLow(item.pctAbove52WeekLow)}</span>
      </div>

      <div style={bucketText}>{item.bucket}</div>
    </Link>
  );
}

const pageShell = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
  padding: "10px 6px 16px",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  color: "#0f172a",
};

const heroCard = {
  maxWidth: 1680,
  margin: "0 auto 8px",
  padding: "10px 12px",
  background:
    "radial-gradient(circle at 16% 12%, rgba(56, 189, 248, 0.18), transparent 30%), linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #334155 100%)",
  color: "white",
  borderRadius: 16,
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.18)",
};

const heroTextWrap = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const eyebrow = {
  marginBottom: 3,
  color: "#93c5fd",
  fontSize: 9,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  fontWeight: 900,
};

const title = {
  margin: 0,
  fontSize: 20,
  letterSpacing: "-0.045em",
  lineHeight: 1,
};

const subtitle = {
  maxWidth: 820,
  margin: "4px 0 0",
  color: "#cbd5e1",
  lineHeight: 1.35,
  fontSize: 11,
};

const metaText = {
  margin: "3px 0 0",
  color: "#93c5fd",
  fontSize: 10,
  fontWeight: 750,
};

const controls = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
};

const searchInput = {
  width: 180,
  padding: "7px 9px",
  border: "1px solid rgba(203, 213, 225, 0.75)",
  borderRadius: 10,
  fontSize: 11,
  background: "white",
  outline: "none",
};

const smallInput = {
  width: 124,
  padding: "7px 9px",
  border: "1px solid rgba(203, 213, 225, 0.75)",
  borderRadius: 10,
  fontSize: 11,
  background: "white",
  outline: "none",
};

function toggleButton(active) {
  return {
    padding: "7px 9px",
    borderRadius: 10,
    border: active ? "1px solid #60a5fa" : "1px solid rgba(203, 213, 225, 0.75)",
    background: active ? "#eff6ff" : "white",
    color: active ? "#1d4ed8" : "#334155",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 850,
  };
}

const clearButton = {
  padding: "7px 9px",
  borderRadius: 10,
  border: "1px solid rgba(203, 213, 225, 0.75)",
  background: "#fff1f2",
  color: "#be123c",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 850,
};

const miniLegend = {
  marginTop: 8,
  display: "grid",
  gridTemplateColumns: "auto minmax(140px, 340px) auto",
  gap: 8,
  alignItems: "center",
  color: "#cbd5e1",
  fontSize: 10,
  fontWeight: 800,
};

const gradientBar = {
  height: 10,
  borderRadius: 999,
  background:
    "linear-gradient(90deg, hsl(120, 82%, 72%) 0%, hsl(60, 82%, 72%) 50%, hsl(0, 82%, 72%) 100%)",
  border: "1px solid rgba(255, 255, 255, 0.35)",
};

const mapCard = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 8,
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  background: "white",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
};

const tableWrap = {
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
};

const mapTable = {
  width: "100%",
  minWidth: 1050,
  borderCollapse: "separate",
  borderSpacing: 0,
};

const themeHeader = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  textAlign: "center",
  padding: "6px 5px",
  fontSize: 9,
  color: "#0f172a",
  background: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
  borderRight: "1px solid #e2e8f0",
  whiteSpace: "normal",
  minWidth: 90,
  maxWidth: 120,
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  lineHeight: 1.15,
};

const mapCell = {
  verticalAlign: "top",
  padding: 4,
  borderBottom: "1px solid #eef2f7",
  borderRight: "1px solid #eef2f7",
  minWidth: 90,
  maxWidth: 120,
};

const tickerTile = {
  display: "block",
  textDecoration: "none",
  padding: "5px 6px",
  borderRadius: 10,
  border: "1px solid",
  minHeight: 46,
};

const tickerTopRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 5,
};

const tickerSymbol = {
  fontSize: 11,
  fontWeight: 950,
  color: "#0f172a",
  letterSpacing: "-0.03em",
};

const typePill = {
  fontSize: 7,
  fontWeight: 950,
  color: "#334155",
  background: "rgba(255,255,255,0.55)",
  border: "1px solid rgba(15,23,42,0.10)",
  borderRadius: 999,
  padding: "1px 4px",
};

const priceRow = {
  marginTop: 4,
  display: "flex",
  justifyContent: "space-between",
  gap: 5,
  color: "#334155",
  fontSize: 8.5,
  fontWeight: 850,
  fontVariantNumeric: "tabular-nums",
};

const bucketText = {
  marginTop: 2,
  fontSize: 8,
  color: "#475569",
  fontWeight: 750,
};

const messageBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 14,
  color: "#475569",
  background: "white",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  fontSize: 12,
};

const errorBox = {
  maxWidth: 1680,
  margin: "0 auto",
  padding: 12,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  borderRadius: 12,
  fontSize: 12,
};